/**
 * « Cette ligne repart-elle sans réponse au motif de refus ? »
 *
 * Module PUR (ni React, ni réseau), donc testé hors ligne. C'est la SEULE règle
 * métier du partage entre les deux circuits de paiement : les factures
 * fournisseurs et les remboursements d'avoirs clients.
 *
 * POURQUOI elle existe. Le motif de refus d'un valideur est très souvent une
 * QUESTION (« quel est ce montant ? », « quel client ? »). Re-soumettre la ligne
 * sans y répondre expose à un second refus pour la même raison, et fait perdre
 * un aller-retour à deux personnes. On n'INTERDIT pas — Valérie peut avoir
 * corrigé la ligne elle-même, ce qui rend la question sans objet — on prévient.
 *
 * POURQUOI ici, et pas dans un composant partagé entre les deux écrans
 * (décision de Valérie du 15/10/2026) : l'écran « À payer » engage des
 * virements, et ce dépôt n'a aucun test d'interface. Partager un gestionnaire
 * React y aurait touché la sélection des lignes et la soumission sans aucun
 * filet. Une fonction pure, elle, entre dans `npm test`.
 */

/** Le minimum qu'une ligne doit porter pour qu'on sache si elle est en attente de réponse. */
export interface LigneRefusable {
  id: string;
  rejet_motif?: string | null;
  reponse_admin?: string | null;
}

/**
 * Comment NOMMER les lignes de cet objet, à l'écran et dans la confirmation.
 *
 * Les deux circuits n'ont pas les mêmes colonnes : une facture porte un
 * fournisseur et un numéro de facture, un avoir porte un client et un numéro
 * d'avoir. Le mot au singulier sert aussi au décompte (« 3 avoir(s) »).
 */
export interface Nommage<T> {
  /** « facture » ou « avoir », au singulier : le texte ajoute « (s) ». */
  objet: string;
  /**
   * Le participe accordé : « refusée(s) » pour une facture, « refusé(s) » pour
   * un avoir. Porté par le nommage et non déduit, parce qu'une généralisation
   * hâtive écrivait « facture refusé(s) » — un texte fautif à l'écran se
   * remarque, et il décrédibilise le reste du message.
   */
  refuses: string;
  /** Le tiers : fournisseur pour une facture, client pour un avoir. */
  tiers: (ligne: T) => string;
  /** Le numéro de pièce. */
  numero: (ligne: T) => string;
}

/**
 * Les lignes qui repartiraient sans réponse, parmi celles choisies.
 *
 * Une ligne compte quand elle porte un motif de refus ET que la réponse est
 * vide. `trim()` est délibéré : une réponse réduite à des espaces n'est pas une
 * réponse, et le champ s'enregistre au `blur`, donc l'espace accidentel arrive.
 */
export function lignesSansReponse<T extends LigneRefusable>(lignes: T[], choisies: Set<string>): T[] {
  return lignes.filter((l) => choisies.has(l.id) && l.rejet_motif && !l.reponse_admin?.trim());
}

/**
 * Le texte de la confirmation. Rend `null` quand il n'y a rien à confirmer —
 * l'appelant soumet alors sans rien demander.
 *
 * Il NOMME les lignes concernées : « 3 lignes sans réponse » obligerait à
 * chercher lesquelles, et devant une liste de virements à préparer on cliquerait
 * « OK » sans vérifier.
 */
export function confirmationSansReponse<T extends LigneRefusable>(
  sansReponse: T[],
  nommage: Nommage<T>,
): string | null {
  if (sansReponse.length === 0) return null;
  const liste = sansReponse.map((l) => `• ${nommage.tiers(l)} : ${nommage.numero(l)}`).join('\n');
  return (
    `${sansReponse.length} ${nommage.objet}(s) ${nommage.refuses} repartent sans réponse au motif :\n\n${liste}\n\n` +
    `Le valideur reverra sa remarque sans réponse en face. Soumettre quand même ?`
  );
}
